import type {
  Alert,
  Phone,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  LargeMenuModalItem,
  LargeMenuModalItemButton,
  LargeMenuModalItemIcon,
  LargeMenuModalItemText,
  LargeMenuModalItemLabel,
  LargeMenuModalItemSubtext,
  LargeMenuModalItemIconButtons,
  IconButton,
  PhoneIcon,
  TrashCanOutlineIcon,
  ExclamationOutlineIcon,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useCallback,
  useMemo,
  useState,
} from "react"
import styles from "./ContactCard.module.scss"
import { RemoveDialog } from "src/components"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import { useUpdateAlert } from "src/hooks"
import {
  RemovePhoneSuccessToast,
  GenericErrorToast,
} from "src/toasts"
import { formatPhoneNumber } from "src/utils"

const polyglotPrefix = "pages.alerts.contacts."

interface Props {
  phone: Phone;
  primaryPhone: Phone | undefined;
  sugarmateAlert: Alert;
}

export function ContactCard(props: Props) {
  const {
    phone,
    primaryPhone,
    sugarmateAlert,
  } = props

  const [
    isRemoveOpen,
    setIsRemoveOpen,
  ] = useState(false)

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const updateAlert = useUpdateAlert(
    primaryPhone,
    sugarmateAlert,
  )

  // removes this phone number from this alert
  const removePhoneNumber = useCallback(
    () => {
      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          if (!updateAlert) {
            throw new Error("cannot remove phone")
          }

          await updateAlert({
            phone_ids: sugarmateAlert.phone_ids.filter((id) => id !== phone.id),
          })

          showToast(
            "remove-phone-success-toast",
            RemovePhoneSuccessToast,
          )
        } catch {
          showToast(
            "remove-phone-error-toast",
            GenericErrorToast,
          )
        }

        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      updateAlert,
      phone,
      sugarmateAlert.phone_ids,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const openRemoveDialog = useCallback(
    () => {
      setIsRemoveOpen(true)
    },
    [setIsRemoveOpen],
  )

  const phoneNumber = useMemo(
    () => formatPhoneNumber(phone.phone_number),
    [phone],
  )

  return (
    <>
      <LargeMenuModalItem className={styles.contactCard}>
        <LargeMenuModalItemButton>
          <LargeMenuModalItemIcon>
            <PhoneIcon />
          </LargeMenuModalItemIcon>
          <LargeMenuModalItemText>
            {phone.nickname && (
              <LargeMenuModalItemLabel>
                {phone.nickname}
              </LargeMenuModalItemLabel>
            )}
            <LargeMenuModalItemSubtext>
              {phoneNumber}
            </LargeMenuModalItemSubtext>
          </LargeMenuModalItemText>
        </LargeMenuModalItemButton>
        <LargeMenuModalItemIconButtons>
          <IconButton
            stylePreset="withoutBackground"
            onClick={openRemoveDialog}
            aria-label={polyglot.t(`${polyglotPrefix}remove_button_aria_label`)}
          >
            <TrashCanOutlineIcon />
          </IconButton>
        </LargeMenuModalItemIconButtons>
        <RemoveDialog
          open={isRemoveOpen}
          setOpen={setIsRemoveOpen}
          remove={removePhoneNumber}
          title={polyglot.t(`${polyglotPrefix}remove_heading`)}
        >
          {polyglot.t(`${polyglotPrefix}remove_description`)}
        </RemoveDialog>
      </LargeMenuModalItem>
      {!phone.confirmed && (
        <div className={styles.pendingVerification}>
          <ExclamationOutlineIcon className={styles.pendingVerificationIcon} />
          <div className={styles.pendingVerificationText}>
            {polyglot.t(`${polyglotPrefix}pending_verification`)}
          </div>
        </div>
      )}
    </>
  )
}
